import { editFieldInSidebar } from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Signals/fieldSidebarSignal';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import { css } from '@emotion/react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const actionsStyle = css`
	display: flex;
	align-items: center;
	gap: 0.375rem;
`;

type Props = {
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	/** Rank of the field among all the fields of the draft, archived ones included. */
	readonly index: number;
	readonly label: string;
	/** True for a field not saved yet: it is removed rather than archived. */
	readonly isNew: boolean;
	readonly canMoveUp: boolean;
	readonly canMoveDown: boolean;
	readonly isReadOnly: boolean;
};

/** Leaf actions of a field row: move it, edit it in the sidebar, archive it (saved) or remove it (new). Nothing is confirmed — the draft is only saved on request. */
const FieldRowActions: React.FC<Props> = ({
	onChange,
	index,
	label,
	isNew,
	canMoveUp,
	canMoveDown,
	isReadOnly,
}) => {
	const handleMoveUpClick = React.useCallback(() => {
		onChange((current) => current.moveField(index, -1));
	}, [onChange, index]);

	const handleMoveDownClick = React.useCallback(() => {
		onChange((current) => current.moveField(index, 1));
	}, [onChange, index]);

	const handleEditClick = React.useCallback(() => {
		editFieldInSidebar(index);
	}, [index]);

	const handleArchiveClick = React.useCallback(() => {
		onChange((current) => current.archiveField(index));
	}, [onChange, index]);

	const handleRemoveClick = React.useCallback(() => {
		onChange((current) => current.removeNewField(index));
	}, [onChange, index]);

	return (
		<div css={actionsStyle}>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				rounded
				icon={<ArrowUp size={16} />}
				aria-label={`Monter le champ ${label}`}
				disabled={isReadOnly || !canMoveUp}
				onClick={handleMoveUpClick}
			/>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				rounded
				icon={<ArrowDown size={16} />}
				aria-label={`Descendre le champ ${label}`}
				disabled={isReadOnly || !canMoveDown}
				onClick={handleMoveDownClick}
			/>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				label="Modifier"
				disabled={isReadOnly}
				onClick={handleEditClick}
			/>
			{isNew ? (
				<Button
					type="button"
					size="sm"
					severity="secondary"
					label="Retirer"
					disabled={isReadOnly}
					onClick={handleRemoveClick}
				/>
			) : (
				<Button
					type="button"
					size="sm"
					severity="danger"
					outlined
					label="Archiver"
					disabled={isReadOnly}
					onClick={handleArchiveClick}
				/>
			)}
		</div>
	);
};

export default FieldRowActions;
